import React from "react";

import { cn } from "./cn";
import { CheckIcon } from "./icons";

export interface DecisionCardOption {
  value: string;
  title: string;
  /** One-line description of the deliverable, not the feature name. */
  description: string;
  /** One-line consequence of choosing this option, e.g. "You can add this later." */
  consequence?: string;
  /** Always selected and not toggleable — renders chosen with an "Included" badge. */
  locked?: boolean;
}

export interface DecisionCardsProps {
  options: DecisionCardOption[];
  selected: string[];
  onToggle: (value: string) => void;
  multiple?: boolean;
}

// Weighty selectable cards, each carrying its one-line consequence so the
// decision reads as "this, and then this happens" rather than a bare label.
export function DecisionCards({ options, selected, onToggle, multiple = true }: DecisionCardsProps) {
  return (
    <div role={multiple ? "group" : "radiogroup"} className="grid gap-3 sm:grid-cols-2">
      {options.map((option) => {
        const isSelected = option.locked || selected.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            role={multiple ? undefined : "radio"}
            aria-pressed={multiple ? isSelected : undefined}
            aria-checked={multiple ? undefined : isSelected}
            aria-disabled={option.locked || undefined}
            onClick={option.locked ? undefined : () => onToggle(option.value)}
            className={cn(
              "flex flex-col gap-2 rounded-lg border p-4 text-left transition-colors",
              isSelected ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40",
              option.locked && "cursor-default"
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <span className="font-medium">{option.title}</span>
              {option.locked ? (
                <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground">
                  Included
                </span>
              ) : (
                <span
                  className={cn(
                    "flex size-4 shrink-0 items-center justify-center rounded-full border",
                    isSelected ? "border-primary bg-primary text-primary-foreground" : "border-border"
                  )}
                >
                  {isSelected && <CheckIcon className="size-3" />}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground">{option.description}</p>
            {option.consequence && <p className="text-xs text-muted-foreground/70">{option.consequence}</p>}
          </button>
        );
      })}
    </div>
  );
}
